import {assert} from 'Asserts';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import deepCopy from 'lib/deepCopy';
import * as ComplexPolymerActions from 'modbio/complex-polymers/ComplexPolymerActions';
import {
  ComplexPolymerConnection,
  ComplexPolymerType,
  ConnectionGraphQL,
  ResolvedAttachmentGraphQL,
  OligoGraphQL,
  SimplePolymerGraphQL,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {
  resolvedAttachmentContainsHelmReference,
  generateEntityAssociationsFromStructure,
  getConnectionInputs,
  getEntityIds,
  getExistingResolvedAttachmentInputs,
} from 'modbio/complex-polymers/ComplexPolymerUtil';

export function getCurrentOligoId(simplePolymers: Array<SimplePolymerGraphQL>, oligo: OligoGraphQL): string {
  const oligos = simplePolymers.filter(
    (polymer) => polymer.entity.__typename === 'SequenceNode' && polymer.helmReference === oligo.helmReference
  );
  assert(oligos.length === 1, 'Expected exactly one matching sequence');
  assert(
    !isInaccessible(oligos[0].entity),
    'All component entities of complex polymer must be accessible to edit'
  );
  return oligos[0].entity.id;
}

export async function swapOligo(
  complexPolymerId: string,
  complexPolymerVersionId: string,
  complexPolymerType: ComplexPolymerType,
  simplePolymers: Array<SimplePolymerGraphQL>,
  connections: Array<ConnectionGraphQL>,
  resolvedAttachments: Array<ResolvedAttachmentGraphQL>,
  oligo: OligoGraphQL,
  selectedIds: Array<string>,
  offset?: number | null
): Promise<void> {
  assert(selectedIds.length === 1, 'Can only set one oligo on a complex polymer');

  const entityIds = getEntityIds(simplePolymers);
  const entityAssociations = generateEntityAssociationsFromStructure(simplePolymers);
  const oligoHelmReference = oligo.helmReference;

  const newEntityAssociations = deepCopy(entityAssociations);
  const currentOligoId = getCurrentOligoId(simplePolymers, oligo);

  if (newEntityAssociations[currentOligoId].length > 1) {
    const refs = newEntityAssociations[currentOligoId].filter(
      (polymerRef) => polymerRef !== oligoHelmReference
    );
    newEntityAssociations[currentOligoId] = refs;
  } else {
    delete newEntityAssociations[currentOligoId];
  }

  // Since this is a swapping action, we re-use the HELM reference of oligo being swapped for the new entity association
  if (newEntityAssociations.hasOwnProperty(selectedIds[0])) {
    newEntityAssociations[selectedIds[0]].push(oligo.helmReference);
  } else {
    newEntityAssociations[selectedIds[0]] = [oligo.helmReference];
  }

  // On an oligo conjugate, all existing connections are removed on swapping oligo
  let newConnections: Array<ComplexPolymerConnection> = [];
  // On an oligo duplex, connections on the strand being swapped are removed, and retained on the strand not swapped out
  if (complexPolymerType === ComplexPolymerType.OLIGO_DUPLEX) {
    const connectionInputs = getConnectionInputs(connections);
    newConnections = connectionInputs.filter(
      (connection) =>
        connection.source.polymer_ref !== oligoHelmReference &&
        connection.target.polymer_ref !== oligoHelmReference
    );
  }

  const newResolvedAttachments = getExistingResolvedAttachmentInputs(
    resolvedAttachments.filter(
      (resolvedAttachment) => !resolvedAttachmentContainsHelmReference(resolvedAttachment, oligoHelmReference)
    )
  );

  // Only a single instance of the current oligo should be removed (there may be multiple instances of the same
  // oligo entity in an oligo duplex, for example)
  const oligoEntityIdx = entityIds.findIndex((entityId) => entityId === currentOligoId);
  const newEntityIds = entityIds.filter((_, idx) => idx !== oligoEntityIdx);
  newEntityIds.push(selectedIds[0]);

  await ComplexPolymerActions.updateComplexPolymer(
    complexPolymerId,
    complexPolymerVersionId,
    newEntityIds,
    newConnections,
    newResolvedAttachments,
    newEntityAssociations,
    complexPolymerType,
    offset ?? undefined
  );
}
